import {
	init,
	type GenericAnalyticWebClientPromise,
} from '@atlaskit/react-ufo/interaction-metrics-init';
import type { Config } from '@atlaskit/react-ufo/config';
import { configure as configureSsrTiming } from '@atlaskit/react-ufo/ssr';

import { fg } from '@confluence/feature-gating';
import { getAnalyticsWebClient } from '@confluence/analytics-web-client';
import { getMark } from '@confluence/performance';
import type { SessionDataType } from '@confluence/session-data';
import { StatsigConfigurations } from '@confluence/statsig-client/entry-points/StatsigConfigurations';
import {
	getLayoutShiftWithAttribution,
	type LayoutShiftAttributionConfig,
} from '@confluence/browser-metrics';

import { additionalPayloadData } from './additionalPayloadData';
import { getSSRDoneTime, getSSRTimings } from './ssr';
import { initUFOTrace } from './ufoTrace';

// If you use any FF during UFO initialization,
// please pass the feature flag to __SSR_INIT_UFO_DATA__ in the confluence/next/packages/ssr-app/src/index.tsx
// and update initUFOWithSsrData as well

const getConfig = (configName: string): any => {
	try {
		return JSON.parse(StatsigConfigurations.getString(configName, ''));
	} catch (error) {
		return {};
	}
};

const getUfoConfig = (): Partial<Config> | undefined => getConfig('confluence_ufo_config');

const getClsAttributionConfig = (): LayoutShiftAttributionConfig =>
	getConfig('confluence_cls_attribution_config');

function runConfigureSsrTiming() {
	configureSsrTiming({
		getDoneMark: () => getMark('CFP-63.ssr-ttr') ?? null, // from confluence/next/packages/browser-metrics/src/initializeBrowserMetrics.ts
		// TODO: add correct functions to `getTimings` and `getFeatureFlags`
		getTimings: () => null,
		getFeatureFlags: () => null,
	});
}

function initializeUFO(initData: typeof window.__SSR_INIT_UFO_DATA__) {
	if (!initData) {
		return;
	}

	const {
		shard,
		cloudId,
		config,
		flags: {
			devToolsEnabled,
			stopVCAtInteractionFinish,
			enableTracePropagation,
			postInteractionLogEnabled,
			enableSegmentHighlighting,
			enableAdditionalPerformanceMarks,
			enableExperimentalHolds,
			shouldCalculateLighthouseMetricsFromTTAI,
			manuallyTrackReactProfilerMounts,
		},
	} = initData;

	runConfigureSsrTiming();

	if (enableTracePropagation) {
		initUFOTrace();
	}

	const perfPushFY25ExperienceRates = {
		'view-page': 1,
		'edit-page': 1,
		'space-overview': 1,
		'confluence-home': 1,
		'advanced-search': 1,
	};

	init(getAnalyticsWebClient() as Promise<GenericAnalyticWebClientPromise>, {
		postInteractionLog: {
			enabled: postInteractionLogEnabled,
			rates: perfPushFY25ExperienceRates,
		},
		experimentalInteractionMetrics: {
			enabled: enableExperimentalHolds,
			rates: perfPushFY25ExperienceRates,
		},
		enableSegmentHighlighting,
		enableAdditionalPerformanceMarks,
		shouldCalculateLighthouseMetricsFromTTAI,
		manuallyTrackReactProfilerMounts,
		product: 'confluence',
		region: shard || 'unknown',
		vc: {
			enabled: true,
			heatmapSize: 200,
			oldDomUpdates: false,
			selectorConfig: {
				id: false,
				role: false,
				className: true,
				testId: true,
			},
			devToolsEnabled,
			stopVCAtInteractionFinish,
			enabledVCRevisions: {
				all: ['fy25.02', 'fy25.03'],
				// AFO-3909 - temporarily enabling TTVC v2 back for edit-page and live-edit, until vc:ratios field is supported in TTVC v3
				// byExperience: {
				// 	'live-edit': ['fy25.03'],
				// 	'edit-page': ['fy25.03'],
				// },
			},
		},
		...config,
		ssr: { getSSRDoneTime, getSSRTimings },
		additionalPayloadData: (interaction) =>
			additionalPayloadData(interaction, {
				cloudId,
				isShardPreProd: shard ? shard.startsWith('conf-prod-us-preprod') : false,
				layoutShiftWithAttribution: fg('confluence_enable_ls_attribution')
					? getLayoutShiftWithAttribution(interaction.ufoName, getClsAttributionConfig())
					: {},
			}),
	});
}

// This function gets called when ufo data is provided by SSR
export function initUFOWithSsrData(initData: typeof window.__SSR_INIT_UFO_DATA__) {
	if (!initData?.config?.enabled) {
		return false;
	}
	initializeUFO(initData);
	return true;
}

export function initUFO(sessionData: SessionDataType) {
	const config = getUfoConfig();
	if (!config?.enabled) {
		return;
	}
	initializeUFO({
		shard: sessionData.shard,
		cloudId: sessionData.cloudId,
		config,
		flags: {
			manuallyTrackReactProfilerMounts: true,
			devToolsEnabled: StatsigConfigurations.isKillSwitchOn('confluence_enable_ttvc_devtool'),
			stopVCAtInteractionFinish: fg('stop_vc_at_interaction_finish'),
			enableTracePropagation: fg('confluence_fe_enable_trace_propagation'),
			postInteractionLogEnabled: true,
			enableSegmentHighlighting: false,
			enableAdditionalPerformanceMarks: false,
			enableExperimentalHolds: fg('ufo-enable-experimental-holds-confluence'),
			shouldCalculateLighthouseMetricsFromTTAI: true,
		},
	});
}
